<template>
  <div>
    <modal :backText="''">
      <div v-if="isSubmitting" class="scorecaster">
        <section class="scorecaster__section scorecaster__section--center">
          <updog-loader class="updog" />
          <p class="loading-message">
            {{
              $t('dashboard.studentDashboard.scoreCasterModal.loadingMessage')
            }}
          </p>
        </section>
      </div>
      <div v-else-if="hasError" class="scorecaster">
        <section class="scorecaster__section scorecaster__section--center">
          <updog-construction class="updog" />
          <p class="scorecaster__body">
            {{ $t('dashboard.studentDashboard.scoreCasterModal.body') }}
          </p>
        </section>
        <footer class="scorecaster__footer">
          <div class="scorecaster__buttons scorecaster__buttons--center">
            <large-button @click.native="handleCloseModal">{{
              $t('general.close')
            }}</large-button>
          </div>
        </footer>
      </div>
      <div v-else-if="showHaveMoreSessions" class="scorecaster">
        <header>
          <scorecaster-banner class="scorecaster-banner" />
          <h1 class="scorecaster__title">
            {{
              $t(
                'dashboard.studentDashboard.scoreCasterModal.moreSessions.header'
              )
            }}
          </h1>
        </header>
        <section class="scorecaster__section scorecaster__section--center">
          <p class="scorecaster__body">
            {{
              $t(
                'dashboard.studentDashboard.scoreCasterModal.moreSessions.body'
              )
            }}
          </p>
        </section>
        <footer class="scorecaster__footer">
          <div class="scorecaster__buttons scorecaster__buttons--center">
            <large-button
              class="scorecaster__buttons-button"
              @click.native="handleCloseModal"
              :showArrow="false"
            >
              {{ $t('general.close') }}
            </large-button>
            <large-button
              class="scorecaster__buttons-button  scorecaster__buttons-button--single"
              @click.native="handleSessionRequest"
              :showArrow="false"
              primary
            >
              {{
                $t(
                  'dashboard.studentDashboard.scoreCasterModal.moreSessions.sessionRequestButton'
                )
              }}
            </large-button>
          </div>
        </footer>
      </div>
      <div v-else-if="step === 1" class="scorecaster">
        <header>
          <scorecaster-banner class="scorecaster-banner" />
          <h1 class="scorecaster__title">
            {{ $t('dashboard.studentDashboard.scoreCasterModal.step-1.title') }}
          </h1>
          <p class="scorecaster__body">
            {{ $t('dashboard.studentDashboard.scoreCasterModal.step-1.body') }}
          </p>
          <p class="scorecaster__body--helper">
            {{
              $t(
                'dashboard.studentDashboard.scoreCasterModal.step-1.bodyHelper'
              )
            }}
          </p>
        </header>

        <footer class="scorecaster__footer">
          <div class="scorecaster__buttons">
            <large-button
              class="scorecaster__buttons-button"
              @click.native="handleCloseModal"
            >
              {{
                $t(
                  'dashboard.studentDashboard.scoreCasterModal.step-1.handleCloseModal'
                )
              }}</large-button
            >
            <large-button
              class="scorecaster__buttons-button scorecaster__buttons-button--primary"
              @click.native="analyzeSessions"
              primary
              :showArrow="false"
              >{{
                $t(
                  'dashboard.studentDashboard.scoreCasterModal.step-1.analyzeSessionsButton'
                )
              }}</large-button
            >
          </div>
        </footer>
      </div>
      <div v-else-if="step === 2" class="scorecaster scorecaster--flex">
        <cross-icon class="cross-icon" @click="handleCloseModal" />
        <header>
          <h1 class="scorecaster__title scorecaster__title-header">
            <span>{{
              $t('dashboard.studentDashboard.scoreCasterModal.step-2.header')
            }}</span>
            <span>{{ summary.overallGrade }}%</span>
          </h1>
        </header>

        <section class="scorecaster__section">
          <p class="scorecaster__body">
            {{ $t('dashboard.studentDashboard.scoreCasterModal.step-2.body') }}
          </p>
          <div class="scorecaster__overview-container">
            <div class="scorecaster__overview">
              <header class="scorecaster__overview-header">
                <h2
                  class="scorecaster__overview-title scorecaster__overview--bold"
                >
                  {{
                    $t(
                      'dashboard.studentDashboard.scoreCasterModal.step-2.strengthsHeader'
                    )
                  }}
                </h2>
              </header>
              <div
                v-for="concept of filteredConceptsToFocusArea('strength')"
                :key="concept.name"
                class="scorecaster__concept"
              >
                <p class="scorecaster__overview-concept">{{ concept.name }}</p>
              </div>
            </div>
            <div class="scorecaster__overview">
              <header class="scorecaster__overview-header">
                <h2
                  class="scorecaster__overview-title scorecaster__overview--bold"
                >
                  {{
                    $t(
                      'dashboard.studentDashboard.scoreCasterModal.step-2.practiceHeader'
                    )
                  }}
                </h2>
              </header>
              <div
                v-for="concept of filteredConceptsToFocusArea('practiceArea')"
                :key="concept.name"
                class="scorecaster__concept"
              >
                <p class="scorecaster__overview-concept">{{ concept.name }}</p>
              </div>
              <span
                class="scorecaster__overview-link"
                @click="showDetailedReport"
              >
                {{
                  $t(
                    'dashboard.studentDashboard.scoreCasterModal.step-2.detailedReport'
                  )
                }}
              </span>
            </div>
            <p class="scorecaster__overview--subtext">
              {{
                $t('dashboard.studentDashboard.scoreCasterModal.step-2.subText')
              }}
            </p>
          </div>
        </section>
        <footer class="scorecaster__footer">
          <div class="scorecaster__buttons">
            <large-button
              class="scorecaster__buttons-button  scorecaster__buttons-button--primary scorecaster__buttons-button--single scorecaster__buttons-button--wide"
              @click.native="handleSessionRequest"
              :showArrow="false"
              primary
            >
              {{
                $t(
                  'dashboard.studentDashboard.scoreCasterModal.step-2.handleSessionRequest'
                )
              }}
            </large-button>
          </div>
        </footer>
      </div>
      <div v-else-if="step === 3" class="scorecaster">
        <header class="scorecaster__practice-area-header">
          <h1 class="scorecaster__title scorecaster__title--center">
            {{
              $t('dashboard.studentDashboard.scoreCasterModal.step-3.header')
            }}
          </h1>
        </header>

        <section class="scorecaster__section--center">
          <div
            v-for="concept of filteredConceptsToFocusArea('practiceArea')"
            :key="concept.name"
          >
            <div class="scorecaster__concept">
              <div class="scorecaster__concept-header">
                <span class="scorecaster__concept-name"
                  >{{ concept.name }}
                </span>
                <div class="scorecaster__concept-scores">
                  <span class="scorecaster__concept-scores--score"
                    >{{
                      $t(
                        'dashboard.studentDashboard.scoreCasterModal.step-3.score'
                      )
                    }}
                    {{ concept.grade }}</span
                  >
                </div>
              </div>
              <div class="scorecaster__concept-recommendations">
                {{
                  $t(
                    'dashboard.studentDashboard.scoreCasterModal.step-3.recommendations'
                  )
                }}
                <ul>
                  <li
                    v-for="detail of filteredFocusAreasToInfoType(
                      concept,
                      'recommendation'
                    )"
                    :key="detail.content"
                    class="scorecaster__concept-recommendations--recommendation"
                  >
                    {{ detail.content }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
        <footer class="scorecaster__footer">
          <div class="scorecaster__buttons">
            <large-button
              class="scorecaster__buttons-button"
              @click.native="handleCloseModal"
              :showArrow="false"
            >
              {{ $t('general.close') }}
            </large-button>
            <large-button
              class="scorecaster__buttons-button  scorecaster__buttons-button--single"
              @click.native="handleSessionRequest"
              :showArrow="false"
              primary
            >
              {{
                $t(
                  'dashboard.studentDashboard.scoreCasterModal.step-3.handleSessionRequest'
                )
              }}
            </large-button>
          </div>
        </footer>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { EVENTS } from '@/consts'
import ScorecasterBanner from '@/assets/scorecaster-banner.svg'
import UpdogConstruction from '@/assets/updog-construction.svg'
import LargeButton from '@/components/LargeButton.vue'
import Modal from '@/components/Modal.vue'
import UpdogLoader from '@/components/UpdogLoader.vue'
import AnalyticsService from '@/services/AnalyticsService'
import NetworkService from '@/services/NetworkService'
import LoggerService from '@/services/LoggerService'
import CrossIcon from '@/assets/cross.svg'
import i18n from '@/i18n'

export default {
  name: 'ScorecasterModal',
  components: {
    Modal,
    LargeButton,
    ScorecasterBanner,
    UpdogLoader,
    UpdogConstruction,
    CrossIcon,
  },
  data() {
    return {
      step: 1,
      isSubmitting: false,
      hasError: false,
      showHaveMoreSessions: false,
      summary: {},
      concepts: [],
    }
  },
  mounted() {
    localStorage.setItem('hasSeenScorecasterModal', true)
    AnalyticsService.captureEvent(EVENTS.SCORECASTER_MODAL_SHOWN)
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
  },
  props: {
    closeModal: { required: true, type: Function },
  },
  methods: {
    handleCloseModal() {
      AnalyticsService.captureEvent(EVENTS.SCORECASTER_MODAL_CLOSED)
      this.closeModal()
    },
    handleSessionRequest() {
      AnalyticsService.captureEvent(EVENTS.SCORECASTER_STUDENT_REQUESTED_HELP)
      // There's a SCORECASTER_MODAL_CLOSED event here so that we can
      // have a clear start and end point for our user journey in PostHog
      AnalyticsService.captureEvent(EVENTS.SCORECASTER_MODAL_CLOSED)
      this.$router.push(`/${i18n.locale}/session/readingWriting/reading/`)
    },
    showDetailedReport() {
      AnalyticsService.captureEvent(
        EVENTS.SCORECASTER_STUDENT_CLICKED_DETAILED_REPORT
      )
      this.step++
    },
    async analyzeSessions() {
      if (this.isSubmitting) return
      this.isSubmitting = true

      try {
        AnalyticsService.captureEvent(EVENTS.SCORECASTER_ANALYSIS_STARTED)
        const {
          data,
          // Only generates report for Reading sessions atm, will be expanded to more subjects
        } = await NetworkService.generateProgressReportForAllSessions()

        AnalyticsService.captureEvent(EVENTS.SCORECASTER_ANALYSIS_RECEIVED)
        this.summary = data.summary
        this.concepts = data.concepts

        // We're going to expect that the AI couldn't come up with a solution
        // based on the student's sessions. The student may need to do more sessions
        // for an analysis
        if (!Object.keys(this.summary).length || !this.concepts.length) {
          this.showHaveMoreSessions = true
          localStorage.removeItem('hasSeenScoreCasterModal')
          AnalyticsService.captureEvent(EVENTS.SCORECASTER_NO_ANALYSIS)
        } else this.step++
      } catch (error) {
        this.hasError = true
        const err = error.response.data.err
        LoggerService.noticeError(err)
        AnalyticsService.captureEvent(EVENTS.SCORECASTER_ANALYSIS_ERROR, {
          error: err,
        })
      } finally {
        this.isSubmitting = false
      }
    },
    getGrade(grade) {
      if (grade < 65) return 1
      return Math.floor((grade - 65) / 10) + 1
    },
    displayGrade(gradeNumber) {
      const grade = this.getGrade(gradeNumber)
      return `${grade}/5`
    },
    filteredConceptsToFocusArea(focusArea) {
      const includedConceptNames = new Set()
      return this.concepts.filter(concept => {
        if (
          !includedConceptNames.has(concept.name) &&
          concept.details.some(detail => detail.focusArea === focusArea)
        ) {
          includedConceptNames.add(concept.name)
          return true
        }
        return false
      })
    },
    filteredFocusAreasToInfoType(concept, infoType) {
      return concept.details.filter(detail => detail.infoType === infoType)
    },
  },
}
</script>

<style lang="scss" scoped>
.scorecaster {
  text-align: initial;

  &__title {
    @include font-category('display-small');
    font-weight: 500;
    margin-top: 1em;
    margin-bottom: 1em;

    &-header {
      @include flex-container(row, space-between, center);
    }

    &--center {
      text-align: center;
    }
  }

  &__subtitle {
    @include font-category('display-small');
    margin-bottom: 0.5em;
    margin-top: 1em;
  }

  &__section--center {
    @include flex-container(column, center, center);
  }

  &__body {
    @include font-category('heading');
    margin-bottom: 0.5em;
    margin-top: 1em;

    &--bold {
      font-weight: 600;
    }

    &--helper {
      color: $c-secondary-grey;
    }
  }

  &__buttons {
    @include flex-container(column, space-between, center);
    margin: 1.6em 0 0.4em 0;
    & span:first-child,
    & button:first-child {
      margin-right: 1em;
    }

    @include breakpoint-above('small') {
      @include flex-container(row, space-between, center);
    }

    &-button {
      margin: 0 auto;
      width: 100%;
      margin-bottom: 1.3em;

      @include breakpoint-above('small') {
        width: 250px;
        margin-bottom: initial;
      }

      &--wide {
        width: 300px;
      }

      &--primary,
      &--single {
        background-color: $c-information-blue;
        &:hover {
          background: darken($c-information-blue, 5%);

          &:disabled {
            background: $c-background-grey;
          }
        }
      }

      &--single {
        margin-right: auto !important;
      }
    }

    &--secondary {
      @include flex-container(row, flex-end, center);
      & button {
        margin: initial;
      }
    }

    &--center {
      @include flex-container(row, center);
    }
  }

  &__overview-container {
    width: 90%;
    margin: 0 auto;

    @include breakpoint-above('small') {
      width: 90%;
      margin-top: 1em;
    }
  }

  &__overview {
    padding: 1em;
    border: 1px solid $c-border-grey;
    border-collapse: collapse;

    &-header {
      @include flex-container(row, space-between, center);
    }

    &-title {
      @include font-category('subheading');
    }

    &--bold {
      font-weight: 600;
    }

    &-concept {
      @include font-category('body');
      margin: 0;
    }

    &-link {
      @include font-category('body');
      color: $c-information-blue;
      margin: 0;
      font-weight: 600;
      &:hover {
        cursor: pointer;
      }
    }

    &--subtext {
      @include font-category('helper-text');
      margin-top: 1em;
    }
  }

  &__practice-area {
    &-header {
      @include flex-container(row, space-between, center);
    }

    &-grade {
      font-size: 22px;
    }
  }

  &__concept {
    margin: 0.5em 0;

    &-header {
      @include flex-container(column, center, initial);
      @include breakpoint-above('small') {
        @include flex-container(row, space-between, center);
      }
    }

    &-name {
      font-weight: 600;
    }

    &-scores {
      @include flex-container(row, initial, center);
      &--score {
        margin-right: 0.2em;
        font-weight: 600;
      }
    }

    &-recommendations {
      margin-top: 0.4em;
      @include breakpoint-above('medium') {
        margin-top: initial;
      }

      &--recommendation {
        color: $c-secondary-grey;
      }
    }
  }

  &--flex {
    @include flex-container(column);
  }
}

.scorecaster-banner {
  width: 100%;
  height: 300px;
}
.error {
  color: $c-error-red;
  margin: 1em 0;
}

.updog {
  width: 120px;
  height: 120px;
}

.loading-message {
  margin-top: 1em;
}

.cross-icon {
  width: 18px;
  margin-right: 1em;
  margin-left: auto;

  &:hover {
    cursor: pointer;
  }

  @include breakpoint-above('medium') {
    margin-bottom: initial;
    margin-right: initial;
    margin-left: auto;
  }
}

html.is-rtl {
  .scorecaster {
    &__buttons {
      & span:first-child,
      & button:first-child {
        margin-left: 1em;
        margin-right: unset;
      }
    }
  }
}
</style>
